import { useCallback, useReducer } from 'react';
import { useMemorizedCacheTags } from '../../../hooks/useMemorizedCacheTag';
import { loadingReducer, loadingReducerInitialState } from '../../../reducer/loadingReducer';
import {
  LessonFlag,
  LessonItemFragment,
  LessonUuidsWithCardsDocument,
  LessonUuidsWithCardsQuery,
  LessonUuidsWithCardsQueryVariables,
  useAssignTeachersToLessonsMutation,
  useCreateLessonMutation,
  useDeleteLessonsMutation,
  useDetachTeachersFromLessonsMutation,
  useSetLessonsFlagMutation,
  useUpdateLessonMutation,
} from '../../../types/planung-graphql-client-defs';
import { isUuid } from '../../../utils/typeguards';
import { urqlClient } from '../../../utils/urqlClient';
import { LessonTableType } from '../TimetableVersion/Table/TimetableVersionLessonsTable';
import { LessonFormType } from '../types';
import { buildDuplicateLessonFormValues, buildEpochPlanInputs, toLessonInput } from '../utils/buildLessonInput';

type LessonCheckbox = 'teachingLoadEnabled' | 'timetableEnabled' | 'elective';

const checkboxFlags: Record<LessonCheckbox, LessonFlag> = {
  teachingLoadEnabled: LessonFlag.TeachingLoadEnabled,
  timetableEnabled: LessonFlag.TimetableEnabled,
  elective: LessonFlag.Elective,
};

export function useDeputateLessonsMutations(
  schoolYearUuid: string | undefined,
  lessonsData: LessonTableType[],
  lessonItems: LessonItemFragment[],
) {
  const [loadingState, dispatch] = useReducer(loadingReducer, loadingReducerInitialState);
  const context = useMemorizedCacheTags(['LESSONS', 'LESSON_CLASS']);

  const [, create] = useCreateLessonMutation();
  const [, update] = useUpdateLessonMutation();
  const [, setFlag] = useSetLessonsFlagMutation();
  const [, assignTeachers] = useAssignTeachersToLessonsMutation();
  const [, detachTeachers] = useDetachTeachersFromLessonsMutation();
  const [, remove] = useDeleteLessonsMutation();

  const addMissingEpochPlans = async (values: LessonFormType) => {
    const inputs = buildEpochPlanInputs(values, lessonsData);
    for (const input of inputs) {
      const res = await create({ schoolYearUuid: schoolYearUuid ?? '', input }, context);
      if (res.error) return res;
    }
    return { success: true };
  };

  const createLesson = async (values: LessonFormType, addEpochPlans = true) => {
    const res = await create(
      {
        schoolYearUuid: schoolYearUuid ?? '',
        curriculumUuid: values.curriculumUuid || null,
        input: toLessonInput(values),
      },
      context,
    );
    if (addEpochPlans && !res.error) {
      await addMissingEpochPlans(values);
    }
    return [{ error: res.error, data: res.data ? [res.data.createLesson] : undefined }];
  };

  const duplicateLesson = async (uuids: string[]) => {
    dispatch({ type: 'SET_LOADING', uuids });
    let result: { error?: unknown; success?: boolean }[] = [];

    const currentData = lessonItems.find((lesson) => lesson.uuid === uuids[0]);
    if (currentData) {
      result = await createLesson(buildDuplicateLessonFormValues(currentData));
    }

    dispatch({ type: 'REMOVE_LOADING', uuids });
    return result;
  };

  const detachLessons = useCallback(
    async (uuids: string[]) => {
      dispatch({ type: 'SET_LOADING', uuids });
      const result = await detachTeachers({ uuids }, context);
      dispatch({ type: 'REMOVE_LOADING', uuids });
      return result;
    },
    [context, detachTeachers],
  );

  const assignLessons = useCallback(
    async (
      uuids: string[],
      teachers: { uuid: string }[],
      curriculumPersonProperties?: { present?: boolean | null; teachingLoad?: boolean | null },
    ) => {
      dispatch({ type: 'SET_LOADING', uuids });

      if (!uuids.length || !uuids.every(isUuid)) {
        return { error: 'No or invalide UUID provided for update' };
      }

      const result = await assignTeachers(
        {
          uuids,
          teachers: teachers.map((teacher) => ({
            personUuid: teacher.uuid,
            present: curriculumPersonProperties?.present ?? true,
            teachingLoad: curriculumPersonProperties?.teachingLoad ?? true,
            writesCertificate: true,
          })),
        },
        context,
      );
      dispatch({ type: 'REMOVE_LOADING', uuids });
      return result;
    },
    [context, assignTeachers],
  );

  const updateLesson = async (values: LessonFormType, uuid: string | unknown) => {
    if (!isUuid(uuid)) {
      return { error: 'No UUID provided for update' };
    }
    dispatch({ type: 'SET_LOADING', uuids: [uuid] });

    const result = await update({ uuid, input: toLessonInput(values) }, context);

    dispatch({ type: 'REMOVE_LOADING', uuids: [uuid] });
    return result;
  };

  const deleteLessons = async (uuids: string[]) => {
    dispatch({ type: 'SET_LOADING', uuids });

    if (!uuids.length || !uuids.every(isUuid)) {
      return { error: 'No or invalide UUID provided for update' };
    }

    const usedInVersionData = await urqlClient
      .query<LessonUuidsWithCardsQuery, LessonUuidsWithCardsQueryVariables>(
        LessonUuidsWithCardsDocument,
        { uuids },
        { requestPolicy: 'network-only' },
      )
      .toPromise();

    const usedUuids = new Set(usedInVersionData.data?.lessonUuidsWithCards);
    // Bug-Fix #3: uuidsFiltered korrekt verwenden
    const uuidsFiltered = uuids.filter((uuid) => !usedUuids.has(uuid));

    if (uuidsFiltered.length > 0) {
      return await remove({ uuids: uuidsFiltered }, context).finally(() => {
        dispatch({ type: 'REMOVE_LOADING', uuids });
      });
    }

    dispatch({ type: 'REMOVE_LOADING', uuids });
    return { error: 'No lessons to delete', success: false };
  };

  const updateCheckboxes = async ({
    type,
    uuids,
    value,
  }: {
    type: LessonCheckbox;
    uuids: string[];
    value: boolean;
  }) => {
    dispatch({ type: 'SET_LOADING', uuids });
    const res = await setFlag({ uuids, flag: checkboxFlags[type], value }, context);
    dispatch({ type: 'REMOVE_LOADING', uuids });
    return res;
  };

  return {
    createLesson,
    updateLesson,
    detachLessons,
    assignLessons,
    deleteLessons,
    updateCheckboxes,
    duplicateLesson,
    loadingState,
    lessonContext: context,
  };
}
